import React, { FC } from "react";
import Box from "@material-ui/core/Box/Box";
import Typography from "@material-ui/core/Typography/Typography";
import Button from "@material-ui/core/Button/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";
import createStyles from "@material-ui/core/styles/createStyles";
import { 
  TrendingUp, 
  People, 
  AttachMoney, 
  Share,
  CheckCircle 
} from "@material-ui/icons";
import { REGISTER_URL } from "./links";

interface AffiliateLandingProps {}

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      minHeight: "100vh",
      background: "linear-gradient(174.27deg, #02172A 29.14%, #10354D 100%)",
      padding: theme.spacing(4),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2),
      },
    },
    heroSection: {
      textAlign: "center",
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(6),
      [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
      },
    },
    heroTitle: {
      fontFamily: "simplifica",
      letterSpacing: 6,
      fontSize: 60,
      color: "#fff",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        fontSize: 44,
      },
    },
    heroSubtitle: {
      fontSize: 24,
      color: "#bdbdbd",
      maxWidth: "800px",
      margin: `0 auto ${theme.spacing(4)}px auto`,
      [theme.breakpoints.down("sm")]: {
        fontSize: 18,
        padding: theme.spacing(0, 2),
      },
    },
    ctaButton: {
      background: "linear-gradient(269.62deg, rgb(93, 196, 178) -27.14%, rgb(16, 53, 77) 24.63%)",
      color: "#fff",
      padding: theme.spacing(1.5, 4),
      fontSize: 18,
      fontWeight: "bold",
      textTransform: "none",
      borderRadius: 25,
      "&:hover": {
        background: "linear-gradient(269.62deg, rgb(93, 196, 178) -10%, rgb(16, 53, 77) 40%)",
      },
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1.2, 3),
        fontSize: 16,
      },
    },
    featuresSection: {
      padding: theme.spacing(6, 0),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(4, 0),
      },
    },
    sectionTitle: {
      fontSize: 36,
      color: "#fff",
      textAlign: "center",
      marginBottom: theme.spacing(4),
      fontFamily: "simplifica",
      letterSpacing: 4,
      [theme.breakpoints.down("sm")]: {
        fontSize: 28,
      },
    },
    featuresGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: theme.spacing(3),
      maxWidth: "1200px",
      margin: "0 auto",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
        gap: theme.spacing(2),
      },
    },
    featureCard: {
      background: "rgba(255, 255, 255, 0.05)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: 12,
      padding: theme.spacing(3),
      textAlign: "center",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      "&:hover": {
        transform: "translateY(-5px)",
        boxShadow: "0 8px 24px rgba(93, 196, 178, 0.2)",
      },
    },
    featureIcon: {
      fontSize: 48,
      color: "#5dc4b2",
      marginBottom: theme.spacing(2),
    },
    featureTitle: {
      fontSize: 20,
      color: "#fff",
      marginBottom: theme.spacing(1),
      fontWeight: "bold",
    },
    featureDescription: {
      fontSize: 14,
      color: "#bdbdbd",
      lineHeight: 1.6,
    },
    benefitsSection: {
      padding: theme.spacing(6, 0),
      background: "rgba(0, 0, 0, 0.2)",
      borderRadius: 16,
      margin: theme.spacing(4, 0),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(4, 2),
        margin: theme.spacing(2, 0),
      },
    },
    benefitsList: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: theme.spacing(0, 4),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(0, 2),
      },
    },
    benefitItem: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(2),
      color: "#bdbdbd",
      fontSize: 16,
      [theme.breakpoints.down("sm")]: {
        fontSize: 14,
      },
    },
    benefitIcon: {
      color: "#5dc4b2",
      marginRight: theme.spacing(2),
      fontSize: 24,
    },
    ctaSection: {
      textAlign: "center",
      padding: theme.spacing(6, 0),
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(4, 0),
      },
    },
    ctaText: {
      fontSize: 20,
      color: "#fff",
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        fontSize: 18,
      },
    },
  })
);

const AffiliateLanding: FC<AffiliateLandingProps> = () => {
  console.log('🎯 AFFILIATE LANDING COMPONENT RENDERED (starfarm-landing)');
  
  const classes = useStyles();

  // Debug: Log when component renders
  React.useEffect(() => {
    console.log('✅✅✅ AffiliateLanding component RENDERED in starfarm-landing!');
    console.log('✅✅✅ Current pathname:', window.location.pathname);
  }, []);

  const handleGetStarted = () => {
    window.location.href = REGISTER_URL;
  };

  const features = [
    {
      icon: <TrendingUp />,
      title: "Earn Commissions",
      description: "Get paid for every artist you refer to StarFarm. Track your earnings in real-time.",
    },
    {
      icon: <People />,
      title: "Help Artists Grow",
      description: "Connect talented artists with a platform that helps them monetize their music.",
    },
    {
      icon: <AttachMoney />,
      title: "Recurring Revenue",
      description: "Earn ongoing commissions as your referrals continue to use the platform.",
    },
    {
      icon: <Share />,
      title: "Easy Sharing",
      description: "Share your unique referral link with artists and track conversions effortlessly.",
    },
  ];

  const benefits = [
    "Competitive commission rates on every referral",
    "Real-time tracking of your referrals and earnings",
    "Flexible payout options when you're ready",
    "Dedicated support to help you succeed",
    "No upfront costs or fees to join",
    "Access to marketing materials and resources",
  ];

  return (
    <Box className={classes.container}>
      {/* Debug indicator */}
      <Box style={{ position: 'absolute', top: 10, right: 10, background: 'red', color: 'white', padding: '10px', zIndex: 9999 }}>
        AFFILIATE LANDING PAGE
      </Box>
      {/* Hero Section */}
      <Box className={classes.heroSection}>
        <Typography variant="h1" className={classes.heroTitle}>
          StarFarm Affiliate Program
        </Typography>
        <Typography variant="h6" className={classes.heroSubtitle}>
          Join our affiliate program and earn commissions by helping artists discover StarFarm. 
          Start earning today by referring talented musicians to our platform.
        </Typography>
        <Button
          className={classes.ctaButton}
          onClick={handleGetStarted}
          size="large"
        >
          Get Started
        </Button>
      </Box>

      {/* Features Section */}
      <Box className={classes.featuresSection}>
        <Typography variant="h2" className={classes.sectionTitle}>
          Why Become an Affiliate?
        </Typography>
        <Box className={classes.featuresGrid}>
          {features.map((feature, index) => (
            <Box key={index} className={classes.featureCard}>
              <Box className={classes.featureIcon}>{feature.icon}</Box>
              <Typography variant="h5" className={classes.featureTitle}>
                {feature.title}
              </Typography>
              <Typography variant="body1" className={classes.featureDescription}>
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Benefits Section */}
      <Box className={classes.benefitsSection}>
        <Typography variant="h2" className={classes.sectionTitle}>
          Program Benefits
        </Typography>
        <Box className={classes.benefitsList}>
          {benefits.map((benefit, index) => (
            <Box key={index} className={classes.benefitItem}>
              <CheckCircle className={classes.benefitIcon} />
              <Typography variant="body1">{benefit}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Final CTA Section */}
      <Box className={classes.ctaSection}>
        <Typography variant="h5" className={classes.ctaText}>
          Ready to start earning? Join our affiliate program today!
        </Typography>
        <Button
          className={classes.ctaButton}
          onClick={handleGetStarted}
          size="large"
        >
          Get Started
        </Button>
      </Box>

      <Box pb={8} />
    </Box>
  );
};

export default AffiliateLanding;

